import { supabase } from '@/integrations/supabase/client';

// FIX-CCS-AUDIO-PRIVACY (PD-7): voice-messages and voice-recordings are now PRIVATE buckets.
// Farmer audio is reached only through short-lived SIGNED URLs. This matches the existing
// signed-URL idiom in src/features/audio/genAudio.ts (createSignedUrl + a named TTL, additive
// null-on-failure) rather than inventing a new pattern.
//
// PIN farmers authenticate with anon + x-session-token (no Supabase JWT); the shared fetch
// wrapper attaches x-session-token to every Supabase request, and the storage RLS policies scope
// signing via get_user_from_session(x-session-token). So a farmer can sign only what the policy
// allows (their own voice-messages; any community voice-recordings). Proven on Dev.

/** Matches genAudio.ts SIGNED_URL_TTL_SECONDS: long enough to (re)play a message on a slow link,
 *  short enough not to be an effectively-permanent link. */
export const SIGNED_AUDIO_TTL_SECONDS = 3600;

/**
 * Extract the object PATH from a stored value, which may be a bare path (new rows) OR a legacy
 * full public/sign URL (rows written before the flip). Backward-compatible so old rows keep working.
 */
export function storagePathFromStored(stored: string | null | undefined, bucket: string): string | null {
  const s = (stored ?? '').trim();
  if (!s) return null;
  for (const marker of [`/object/public/${bucket}/`, `/object/sign/${bucket}/`, `/object/${bucket}/`]) {
    const i = s.indexOf(marker);
    if (i >= 0) return s.slice(i + marker.length).split('?')[0];
  }
  if (s.startsWith(`${bucket}/`)) return s.slice(bucket.length + 1);
  return s; // already a bare path
}

/**
 * Resolve a short-lived SIGNED URL for a private-bucket audio object. Accepts a stored path or a
 * legacy full URL. Additive: returns null (never throws) when there is nothing to resolve or signing
 * is not permitted, so a playback UI degrades gracefully instead of breaking.
 */
export async function getSignedAudioUrl(
  stored: string | null | undefined,
  bucket: string,
  ttl: number = SIGNED_AUDIO_TTL_SECONDS,
): Promise<string | null> {
  const path = storagePathFromStored(stored, bucket);
  if (!path) return null;
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, ttl);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

// The helper is bucket-agnostic; this alias reads better at photo/avatar call sites
// (FIX-CCS-PRIVACY-COMPLETE: photos + avatars are now private too).
export const getSignedStorageUrl = getSignedAudioUrl;

/**
 * Batch-sign many object paths in ONE request (used by photo grids). Accepts stored values that may
 * be paths or legacy full URLs; entries already an http URL are passed through. Returns a
 * path/stored-value -> signed URL map; additive (missing entries just won't have a signed URL).
 */
export async function signStorageMap(
  bucket: string,
  stored: (string | null | undefined)[],
  ttl: number = SIGNED_AUDIO_TTL_SECONDS,
): Promise<Record<string, string>> {
  const map: Record<string, string> = {};
  const paths = Array.from(new Set(
    stored.map(s => storagePathFromStored(s, bucket)).filter((p): p is string => !!p),
  ));
  if (paths.length === 0) return map;
  try {
    const { data } = await supabase.storage.from(bucket).createSignedUrls(paths, ttl);
    (data || []).forEach((d, i) => { if (d?.signedUrl) map[paths[i]] = d.signedUrl; });
  } catch { /* additive: return what we have */ }
  return map;
}
